import { useEffect, useState } from 'react';
import { Page, Card, FormLayout, Select, TextField, Button, Toast, Frame, BlockStack } from '@shopify/polaris';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';

interface SettingsResponse {
  currency: string;
  timezone: string;
  settings: {
    cost_method?: string;
    tax_rate?: number;
  };
}

export default function Settings() {
  const [currency, setCurrency] = useState('USD');
  const [timezone, setTimezone] = useState('UTC');
  const [costMethod, setCostMethod] = useState('fifo');
  const [taxRate, setTaxRate] = useState('0');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    client.get<SettingsResponse>(Endpoints.settings.get).then((res) => {
      setCurrency(res.data.currency);
      setTimezone(res.data.timezone);
      setCostMethod(res.data.settings?.cost_method ?? 'fifo');
      setTaxRate(String(res.data.settings?.tax_rate ?? 0));
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.put(Endpoints.settings.update, {
        currency,
        timezone,
        settings: {
          cost_method: costMethod,
          tax_rate: parseFloat(taxRate || '0'),
        },
      });
      setToast('Settings saved');
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Frame>
      <Page title="Settings">
        <Card>
          <BlockStack gap="400">
            <FormLayout>
              <FormLayout.Group>
                <TextField label="Currency" value={currency} onChange={setCurrency} autoComplete="off" />
                <TextField label="Timezone" value={timezone} onChange={setTimezone} autoComplete="off" />
              </FormLayout.Group>
              <Select
                label="Cost accounting method"
                options={[
                  { label: 'FIFO (first in, first out)', value: 'fifo' },
                  { label: 'LIFO (last in, first out)', value: 'lifo' },
                  { label: 'Weighted average', value: 'average' },
                ]}
                value={costMethod}
                onChange={setCostMethod}
              />
              <TextField
                label="Default tax rate"
                type="number"
                value={taxRate}
                onChange={setTaxRate}
                helpText="Decimal (e.g. 0.1 for 10%)."
                autoComplete="off"
              />
            </FormLayout>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              Save settings
            </Button>
          </BlockStack>
        </Card>
      </Page>
      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </Frame>
  );
}
